import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Play, RotateCcw, ShieldAlert, CheckCircle2, Loader2,
  AlertTriangle, Hash, ArrowRight, Bot, Pause, ExternalLink, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { agentsApi, formatApiError } from "../../lib/ledgerApi";

const StateChip = ({ state }) => {
  const map = {
    queued: { cls: "border-border bg-muted text-muted-foreground", label: "queued", icon: Pause },
    executing: { cls: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400", label: "executing", icon: Loader2, spin: true },
    executed: { cls: "border-status-ok/40 bg-status-ok/10 text-status-ok", label: "executed", icon: CheckCircle2 },
    held: { cls: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300", label: "held", icon: ShieldAlert },
    pending_approval: { cls: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300", label: "pending approval", icon: ShieldAlert },
    error: { cls: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered", label: "error", icon: AlertTriangle },
  };
  const v = map[state] || map.queued;
  const Icon = v.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-widest ${v.cls}`}>
      <Icon className={`w-3 h-3 ${v.spin ? "animate-spin" : ""}`} strokeWidth={1.5} />
      {v.label}
    </span>
  );
};

export default function DemoAgents() {
  const [scenario, setScenario] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState({});
  const [verifications, setVerifications] = useState({});
  const pollRef = useRef(null);

  const stop = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const poll = async (id) => {
    try {
      const s = await agentsApi.getLiveScenario(id);
      setScenario(s);
      if (s.status !== "running") stop();
    } catch (e) {
      stop();
    }
  };

  const onStart = async () => {
    setBusy(true);
    setVerifications({});
    try {
      const r = await agentsApi.startLiveScenario();
      setScenario({
        id: r.scenario_id,
        agent_id: r.agent_id,
        ephemeral: r.ephemeral,
        merchant_vpa: r.merchant_vpa,
        status: "running",
        steps: [
          { step: 1, amount: "3.00", description: "ResearchAgent: small dataset", status: "queued" },
          { step: 2, amount: "7.00", description: "MerchantAgent: paid API call", status: "queued" },
          { step: 3, amount: "12.50", description: "Coordinator: aggregated bundle", status: "queued" },
        ],
      });
      stop();
      pollRef.current = setInterval(() => poll(r.scenario_id), 500);
      toast.success("Scenario started", { description: "Streaming step events…" });
    } catch (e) {
      toast.error("Start failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onReset = () => {
    stop();
    setScenario(null);
    setVerifications({});
  };

  const onVerify = async (receiptId) => {
    setVerifying((v) => ({ ...v, [receiptId]: true }));
    try {
      const r = await agentsApi.verifyReceipt(receiptId);
      setVerifications((v) => ({ ...v, [receiptId]: r }));
      toast.success(r.valid ? "Receipt verified" : "Receipt INVALID", {
        description: `sig=${r.network_signature_valid} on_ledger=${r.on_ledger}`,
      });
    } catch (e) {
      toast.error("Verify failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setVerifying((v) => ({ ...v, [receiptId]: false }));
    }
  };

  useEffect(() => () => stop(), []);

  return (
    <div className="max-w-5xl px-6 md:px-10 py-10 md:py-14" data-testid="demo-agents-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            / Demo · Agents
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
            Live agent payments
          </h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Provisions an ephemeral agent under your business, runs three small
            x402 payments to <code className="font-mono">globex@neopay</code> with
            2.5s gaps so velocity-fraud doesn't fire, and revokes the agent at the
            end. Each step transitions <em>queued → executing → executed</em> and
            mints a network-signed receipt you can verify publicly.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onStart}
            disabled={busy || (scenario && scenario.status === "running")}
            data-testid="demo-start-btn"
            className="rounded-sm"
          >
            {busy
              ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
              : <Play className="w-3 h-3 mr-1.5" strokeWidth={1.5} />}
            Run scenario
          </Button>
          {scenario && (
            <Button
              onClick={onReset}
              data-testid="demo-reset-btn"
              variant="outline"
              className="rounded-sm"
            >
              <RotateCcw className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Reset
            </Button>
          )}
        </div>
      </div>

      <div className="mb-6 border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 text-xs text-amber-700 dark:text-amber-300 flex gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
        <div>
          <strong>Demo only.</strong> The agent created here is ephemeral and
          revoked after the scenario completes. Real payments settle on the
          ledger using your business's wallets — keep amounts small.
        </div>
      </div>

      {scenario && (
        <>
          <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
            <Cell label="scenario" value={scenario.id?.slice(0, 12) + "…"} />
            <Cell label="agent" value={scenario.agent_id?.slice(0, 8) + "…"} />
            <Cell label="merchant" value={scenario.merchant_vpa} />
            <Cell label="status" value={scenario.status} />
          </div>

          <div className="space-y-3">
            {scenario.steps.map((s, idx) => (
              <div
                key={s.step}
                data-testid={`demo-step-${s.step}`}
                className="border border-border rounded-sm bg-card p-4 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 border border-border rounded-sm flex items-center justify-center font-mono text-sm">
                      {s.step}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{s.description}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        ${s.amount} USD → {scenario.merchant_vpa}
                      </div>
                    </div>
                  </div>
                  <StateChip state={s.status || "queued"} />
                </div>

                {/* Pipeline mini-bar */}
                <div className="grid grid-cols-5 gap-1 mb-3">
                  <PipeStep label="signed" active={s.status !== "queued"} />
                  <PipeStep label="policy" active={s.status !== "queued"} />
                  <PipeStep label="fraud" active={["executing", "executed", "held", "error"].includes(s.status)} />
                  <PipeStep label="settled" active={s.status === "executed"} />
                  <PipeStep label="receipt" active={!!s.receipt_id} />
                </div>

                {s.tx_id && (
                  <div className="text-[10px] font-mono text-muted-foreground flex flex-wrap gap-x-3">
                    <span>tx: <Link className="hover:text-foreground" to={`/tx/${s.tx_id}`}>{s.tx_id.slice(0, 14)}…</Link></span>
                    {s.block_number != null && (
                      <span>block #{s.block_number}</span>
                    )}
                    {s.intent_id && <span>intent: {s.intent_id.slice(0, 14)}…</span>}
                  </div>
                )}

                {s.receipt_id && (
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-mono text-[10px] text-muted-foreground inline-flex items-center gap-1.5">
                      <Receipt className="w-3 h-3" strokeWidth={1.5} />
                      {s.receipt_id.slice(0, 18)}…
                    </div>
                    <div className="flex items-center gap-2">
                      {verifications[s.receipt_id] && (
                        <span
                          data-testid={`demo-verify-result-${s.step}`}
                          className={`px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
                            verifications[s.receipt_id].valid
                              ? "border-status-ok/40 bg-status-ok/10 text-status-ok"
                              : "border-status-tampered/40 bg-status-tampered/10 text-status-tampered"
                          }`}
                        >
                          {verifications[s.receipt_id].valid ? "valid" : "invalid"}
                        </span>
                      )}
                      <button
                        onClick={() => onVerify(s.receipt_id)}
                        disabled={!!verifying[s.receipt_id]}
                        data-testid={`demo-verify-btn-${s.step}`}
                        className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                      >
                        {verifying[s.receipt_id]
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} />}
                        Verify receipt
                      </button>
                    </div>
                  </div>
                )}

                {s.error && (
                  <div className="mt-3 pt-3 border-t border-status-tampered/40 text-[10px] font-mono text-status-tampered">
                    <strong>{s.error.code}:</strong> {s.error.message || JSON.stringify(s.error)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {!scenario && (
        <div className="border border-border rounded-sm bg-card p-12 text-center" data-testid="demo-empty">
          <Bot className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-sm font-medium mb-1">Ready to run</div>
          <div className="text-xs text-muted-foreground max-w-md mx-auto">
            Click <strong>Run scenario</strong> above to provision an ephemeral
            agent and watch it stream three real x402 payments through the
            ledger in real time.
          </div>
        </div>
      )}
    </div>
  );
}

const Cell = ({ label, value }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
    <div className="truncate" title={value}>{value || "—"}</div>
  </div>
);

const PipeStep = ({ label, active }) => (
  <div className={`px-2 py-1 border rounded-sm text-[9px] uppercase tracking-widest text-center font-mono transition-colors ${
    active
      ? "border-foreground bg-foreground text-background"
      : "border-border bg-muted text-muted-foreground"
  }`}>
    {label}
  </div>
);
